"use client"

import React, { useState, useEffect } from 'react'
import connectionMatchingService from '../../../services/connectionMatchingService'
import { BusinessIcon, CalendarIcon, PartnerIcon } from '../../icons'
import { Award, Star, FileText } from 'lucide-react'

interface ReviewStepProps {
  uid: string
  onNext: () => void
  guidelinesAccepted: boolean
  setGuidelinesAccepted: (accepted: boolean) => void
}

export default function ReviewStep({ uid, onNext, guidelinesAccepted, setGuidelinesAccepted }: ReviewStepProps) {
  const [profileData, setProfileData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [topMatches, setTopMatches] = useState<any[]>([])
  const [matchesLoading, setMatchesLoading] = useState(false)

  useEffect(() => {
    loadProfileData()
  }, [uid])

  useEffect(() => {
    if (profileData && uid) {
      loadTopMatches()
    }
  }, [profileData, uid])

  const loadProfileData = async () => {
    try {
      const { doc, getDoc } = await import('firebase/firestore')
      const { db } = await import('../../../firebase')
      
      const profileRef = doc(db, 'userProfiles', uid)
      const profileSnap = await getDoc(profileRef)
      
      if (profileSnap.exists()) {
        setProfileData(profileSnap.data())
      }
    } catch (error) {
      console.error('Error loading profile data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTopMatches = async () => {
    try {
      setMatchesLoading(true)
      const { collection, query, where, getDocs, limit } = await import('firebase/firestore')
      const { db } = await import('../../../firebase')
      
      // Haal eerste 3 "open voor netwerk" gebruikers op
      const openForConnectionsQuery = query(
        collection(db, 'openForConnections'),
        where('isActive', '==', true),
        limit(3)
      )
      const snapshot = await getDocs(openForConnectionsQuery)
      
      const openUsers = snapshot.docs.map(doc => ({
        userId: doc.data().userId,
        userName: doc.data().userName,
        userEmail: doc.data().userEmail,
        userPhoto: doc.data().userPhoto,
        userCompany: doc.data().userCompany,
        userRegion: doc.data().userRegion,
        userRegions: doc.data().userRegions || [],
        userBio: doc.data().userBio,
        subscriptionTier: doc.data().subscriptionTier || 'free'
      })).filter(u => u.userId !== uid) // Filter current user

      if (openUsers.length > 0 && profileData) {
        // Voeg match data toe
        const enrichedUsers = await connectionMatchingService.enrichUsersWithMatchData(
          uid,
          profileData,
          openUsers
        )

        // Sorteer op match score en neem top 3
        enrichedUsers.sort((a, b) => {
          if (a.matchData.sharedConnections !== b.matchData.sharedConnections) {
            return b.matchData.sharedConnections - a.matchData.sharedConnections
          }
          return b.matchData.matchScore - a.matchData.matchScore
        })

        setTopMatches(enrichedUsers.slice(0, 3))
      }
    } catch (error) {
      console.error('Error loading top matches:', error)
    } finally {
      setMatchesLoading(false)
    }
  }

  // Note: handleComplete functionality moved to GoLiveButton component

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="space-y-4">
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          ✅ Review & Afronden
        </h2>
        <p className="text-gray-600">
          Je staat live, je hebt zojuist 50 punten ontvangen en de Starter badge ontgrendeld
        </p>
      </div>

      {/* Profile Summary */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">👤 Profiel Samenvatting</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Basis Informatie</h4>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Naam:</span> {profileData?.displayName || 'Niet ingevuld'}</div>
              <div><span className="font-medium">Expertise:</span> {profileData?.expertise?.join(', ') || 'Niet ingevuld'}</div>
              <div><span className="font-medium">Regio:</span> {profileData?.region || 'Niet ingevuld'}</div>
              <div><span className="font-medium">Talen:</span> {profileData?.languages?.join(', ') || 'Niet ingevuld'}</div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Doelen</h4>
            <div className="space-y-2 text-sm">
              {profileData?.goals ? (
                Object.entries(profileData.goals).map(([key, value]) => (
                  <div key={key}>
                    <span className="font-medium">{key}:</span> {value ? '✅' : '❌'}
                  </div>
                ))
              ) : (
                <div>Niet ingevuld</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Business Summary */}
      {profileData?.idealClient && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2"><BusinessIcon size={20} weight="regular" className="text-gray-700" /> Business Profiel</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Ideale Klant</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Industrie:</span> {profileData.idealClient.industry?.join(', ') || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Bedrijfsgrootte:</span> {profileData.idealClient.companySize || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Budget:</span> {profileData.idealClient.budgetRange || 'Niet ingevuld'}</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Waarde Propositie</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Aanbod:</span> {profileData.offersValue?.join(', ') || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Uitdagingen:</span> {profileData.challenges?.join(', ') || 'Niet ingevuld'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Preferences Summary */}
      {profileData?.eventPrefs && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2"><CalendarIcon size={20} weight="regular" className="text-gray-700" /> Event Voorkeuren</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Events</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Mode:</span> {profileData.eventPrefs.mode || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Regio's:</span> {profileData.eventPrefs.regions?.join(', ') || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Thema's:</span> {profileData.eventPrefs.themes?.join(', ') || 'Niet ingevuld'}</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Content</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Podcast ervaring:</span> {profileData.contentPrefs?.podcastExp || 'Niet ingevuld'}</div>
                <div><span className="font-medium">Topics:</span> {profileData.contentPrefs?.topics?.join(', ') || 'Niet ingevuld'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rewards Preview */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="font-medium text-green-800 mb-2 flex items-center gap-1.5"><Award size={18} className="text-green-700" /> Je verdient:</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <Star size={24} className="text-green-600" />
            <div>
              <div className="text-sm font-medium text-green-800">50 punten</div>
              <div className="text-xs text-green-600">Voor het voltooien van onboarding</div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Award size={24} className="text-green-600" />
            <div>
              <div className="text-sm font-medium text-green-800">Starter badge</div>
              <div className="text-xs text-green-600">Je eerste community badge</div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Matches Preview */}
      {matchesLoading ? (
        <div className="bg-white rounded-lg border p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      ) : topMatches.length > 0 ? (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
            <PartnerIcon size={20} weight="regular" className="text-blue-700" />
            <span>Je eerste matches staan klaar!</span>
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            Op basis van je profiel hebben we al {topMatches.length} relevante connecties voor je gevonden
          </p>
          <div className="space-y-2">
            {topMatches.map((match) => (
              <div key={match.userId} className="bg-white rounded-lg border border-blue-100 p-3 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {match.userPhoto ? (
                    <img src={match.userPhoto} alt={match.userName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">
                        {match.userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm truncate">{match.userName}</div>
                    {match.userCompany && (
                      <div className="text-xs text-gray-600 truncate">{match.userCompany}</div>
                    )}
                    {match.matchData.matchReasons.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                      {match.matchData.matchReasons.slice(0, 2).map((reason: string, idx: number) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded">
                          {reason}
                        </span>
                      ))}
                      </div>
                    )}
                  </div>
                </div>
                {match.matchData.matchScore > 0 && (
                  <div className="text-right flex-shrink-0 ml-2">
                    <div className="text-xs font-semibold text-blue-700">{match.matchData.matchScore}%</div>
                    <div className="text-[10px] text-blue-600">match</div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <p className="text-xs text-blue-600">
              Na het voltooien van onboarding kun je alle matches bekijken in de "Open voor netwerk" lijst
            </p>
          </div>
        </div>
      ) : null}

      {/* Guidelines */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-1.5"><FileText size={18} className="text-blue-700" /> Community Guidelines</h3>
        <div className="text-sm text-blue-700 space-y-2 mb-4">
          <p>• Wees respectvol en ondersteunend naar andere leden</p>
          <p>• Deel waardevolle content en ervaringen</p>
          <p>• Geen spam of ongepaste content</p>
          <p>• Houd je aan de algemene voorwaarden</p>
        </div>
        
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={guidelinesAccepted}
            onChange={(e) => setGuidelinesAccepted(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-blue-800">
            Ik ga akkoord met de community guidelines
          </span>
        </label>
      </div>

      {/* Note: Navigation handled by parent OnboardingWizard */}
      
      {/* Guidelines validation message */}
      {!guidelinesAccepted && (
        <div className="text-sm text-red-600 mt-4">
          Je moet akkoord gaan met de community guidelines om door te gaan
        </div>
      )}
    </div>
  )
}
